import { clearTokenData } from "@features/auth/utils";
import { ErrorResponse } from "@shared/types/commonTypes";
import { showToast } from "@shared/lib";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  GETRequest,
  POSTRequest,
  PUTRequest,
  PATCHRequest,
  DELETERequest,
  API_URL, // Import DELETERequest
} from "@/api/baseAPI";

const doNotLogoutEndpoints = [
  "/api/user/me/password",
  "/api/user/me/password/verify",
  "/api/auth/ott/login",
];

const AUTH_FAILURE_WINDOW_MS = 10_000;
const AUTH_FAILURE_LOGOUT_THRESHOLD = 3;
let recentAuthFailureTimestamps: number[] = [];

interface UseApiRequest {
  GET: <T>(
    endpoint: string,
    addUTMParams?: boolean,
    skillFullRoute?: boolean,
    suppressToastErrors?: boolean
  ) => Promise<T>;
  POST: <T>(
    endpoint: string,
    data: object,
    addUTMParams?: boolean,
    logoutOnFail?: boolean,
    suppressToastErrors?: boolean,
    signal?: AbortSignal
  ) => Promise<T>;
  PUT: <T>(
    endpoint: string,
    data: object,
    addUTMParams?: boolean,
    suppressToastErrors?: boolean
  ) => Promise<T>;
  PATCH: <T>(
    endpoint: string,
    data: object,
    addUTMParams?: boolean,
    suppressToastErrors?: boolean
  ) => Promise<T>;
  DELETE: <T>(
    endpoint: string,
    addUTMParams?: boolean,
    data?: object,
    suppressToastErrors?: boolean
  ) => Promise<T>;
}

const useApiRequest = (): UseApiRequest => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleError = (
    error: ErrorResponse,
    logoutOnFail: boolean = true,
    suppressToastErrors: boolean = false
  ) => {
    if (error.code === "ERR_CANCELED") {
      return;
    }

    if (error.response) {
      const { status, data, config } = error.response;
      const message = data?.error?.display || "Something went wrong!";
      const isInlineError = data?.error?.inline;
      if (!isInlineError && !suppressToastErrors) {
        dispatch(
          showToast({
            message: message,
            type: "error",
          })
        );
      }

      const doNotLogout =
        doNotLogoutEndpoints.find(
          (endPoint: string) => `${API_URL}${endPoint}` === config.url
        ) !== undefined;

      if ((status === 401 || status === 403) && logoutOnFail && !doNotLogout) {
        // Avoid immediate logout on a single transient auth failure.
        // We keep a rolling 10-second window of 401/403 timestamps and only
        // force logout when the threshold is reached (3 hits). This reduces
        // accidental session drops while still protecting invalid sessions.
        const now = Date.now();
        recentAuthFailureTimestamps = recentAuthFailureTimestamps.filter(
          (timestamp) => now - timestamp <= AUTH_FAILURE_WINDOW_MS
        );
        recentAuthFailureTimestamps.push(now);

        if (recentAuthFailureTimestamps.length >= AUTH_FAILURE_LOGOUT_THRESHOLD) {
          recentAuthFailureTimestamps = [];
          clearTokenData(false);
        }
      }
    } else if (!suppressToastErrors) {
      dispatch(
        showToast({
          message: t("common.networkError"),
          type: "error",
        })
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccess = (response: any) => {
    if (response?.message) {
      const message = response?.message;
      dispatch(
        showToast({
          message: message,
          type: "success",
        })
      );
    }
  };

  const GET = async <T>(
    endpoint: string,
    addUTMParams = false,
    skillFullRoute = false,
    suppressToastErrors = false
  ): Promise<T> => {
    try {
      return await GETRequest(endpoint, addUTMParams, skillFullRoute);
    } catch (response) {
      handleError(response as ErrorResponse, true, suppressToastErrors);
      throw response;
    }
  };

  const POST = async <T>(
    endpoint: string,
    data: object | FormData,
    addUTMParams = false,
    logoutOnFail = true,
    suppressToastErrors = false,
    signal?: AbortSignal
  ): Promise<T> => {
    try {
      const response = await POSTRequest<T>(
        endpoint,
        data,
        addUTMParams,
        signal
      );
      handleSuccess(response);
      return response;
    } catch (response) {
      handleError(response as ErrorResponse, logoutOnFail, suppressToastErrors);
      throw response;
    }
  };

  const PUT = async <T>(
    endpoint: string,
    data: object,
    addUTMParams = false,
    suppressToastErrors = false
  ): Promise<T> => {
    try {
      const response = await PUTRequest<T>(endpoint, data, addUTMParams);
      handleSuccess(response);
      return response;
    } catch (error) {
      handleError(error as ErrorResponse, true, suppressToastErrors);
      throw error;
    }
  };

  const PATCH = async <T>(
    endpoint: string,
    data: object,
    addUTMParams = false,
    suppressToastErrors = false
  ): Promise<T> => {
    try {
      const response = await PATCHRequest<T>(endpoint, data, addUTMParams);
      handleSuccess(response);
      return response;
    } catch (error) {
      handleError(error as ErrorResponse, true, suppressToastErrors);
      throw error;
    }
  };

  const DELETE = async <T>(
    endpoint: string,
    addUTMParams = false,
    data: object = {},
    suppressToastErrors = false
  ): Promise<T> => {
    try {
      const response = await DELETERequest<T>(endpoint, addUTMParams, data);
      handleSuccess(response);
      return response;
    } catch (error) {
      handleError(error as ErrorResponse, true, suppressToastErrors);
      throw error;
    }
  };

  return { GET, POST, PUT, PATCH, DELETE };
};

export default useApiRequest;
